/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { DeleteRequestSuccess } from '../models/delete-request-success';
import { FeatureRequestSingleRecord } from '../models/feature-request-single-record';
import { FeatureRequestSuccess } from '../models/feature-request-success';
import { GetPopularFeaturesData } from '../models/get-popular-features-data';
import { PostPopularFeatureVotes } from '../models/post-popular-feature-votes';
import { UpdateFeatureRequest } from '../models/update-feature-request';

@Injectable({
  providedIn: 'root',
})
export class FeatureRequestApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getPopularFeatures
   */
  static readonly GetPopularFeaturesPath = '/api/v1/feature';

  /**
   * This is a public API to get the popular features based on the vote count.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPopularFeatures()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPopularFeatures$Response(params?: {

    /**
     * page number
     */
    page?: string;

    /**
     * pageSize
     */
    pageSize?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<GetPopularFeaturesData>> {

    const rb = new RequestBuilder(this.rootUrl, FeatureRequestApiService.GetPopularFeaturesPath, 'get');
    if (params) {
      rb.query('page', params.page, {});
      rb.query('pageSize', params.pageSize, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GetPopularFeaturesData>;
      })
    );
  }

  /**
   * This is a public API to get the popular features based on the vote count.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPopularFeatures$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPopularFeatures(params?: {

    /**
     * page number
     */
    page?: string;

    /**
     * pageSize
     */
    pageSize?: string;
    context?: HttpContext
  }
): Observable<GetPopularFeaturesData> {

    return this.getPopularFeatures$Response(params).pipe(
      map((r: StrictHttpResponse<GetPopularFeaturesData>) => r.body as GetPopularFeaturesData)
    );
  }

  /**
   * Path part for operation submitFeatureRequest
   */
  static readonly SubmitFeatureRequestPath = '/api/v1/feature';

  /**
   * This is a public API to store the feature reqeust for the RBAC
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `submitFeatureRequest()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  submitFeatureRequest$Response(params: {
    context?: HttpContext
    body: {

/**
 * Title of the feature
 */
'title'?: string;

/**
 * summary of the feature
 */
'description': string;
}
  }
): Observable<StrictHttpResponse<FeatureRequestSuccess>> {

    const rb = new RequestBuilder(this.rootUrl, FeatureRequestApiService.SubmitFeatureRequestPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FeatureRequestSuccess>;
      })
    );
  }

  /**
   * This is a public API to store the feature reqeust for the RBAC
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `submitFeatureRequest$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  submitFeatureRequest(params: {
    context?: HttpContext
    body: {

/**
 * Title of the feature
 */
'title'?: string;

/**
 * summary of the feature
 */
'description': string;
}
  }
): Observable<FeatureRequestSuccess> {

    return this.submitFeatureRequest$Response(params).pipe(
      map((r: StrictHttpResponse<FeatureRequestSuccess>) => r.body as FeatureRequestSuccess)
    );
  }

  /**
   * Path part for operation apiV1IdGet
   */
  static readonly ApiV1IdGetPath = '/api/v1/{id}';

  /**
   * This is a public api and will be used to get the record based on the ID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1IdGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1IdGet$Response(params: {

    /**
     * Unique Id of the feature request
     */
    id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<FeatureRequestSingleRecord>> {

    const rb = new RequestBuilder(this.rootUrl, FeatureRequestApiService.ApiV1IdGetPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FeatureRequestSingleRecord>;
      })
    );
  }

  /**
   * This is a public api and will be used to get the record based on the ID
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `apiV1IdGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1IdGet(params: {

    /**
     * Unique Id of the feature request
     */
    id: string;
    context?: HttpContext
  }
): Observable<FeatureRequestSingleRecord> {

    return this.apiV1IdGet$Response(params).pipe(
      map((r: StrictHttpResponse<FeatureRequestSingleRecord>) => r.body as FeatureRequestSingleRecord)
    );
  }

  /**
   * Path part for operation apiV1IdDelete
   */
  static readonly ApiV1IdDeletePath = '/api/v1/{id}';

  /**
   * This is a public API and used for delete the feature request record.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1IdDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1IdDelete$Response(params: {

    /**
     * Unique Id of the feature request
     */
    id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<DeleteRequestSuccess>> {

    const rb = new RequestBuilder(this.rootUrl, FeatureRequestApiService.ApiV1IdDeletePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DeleteRequestSuccess>;
      })
    );
  }

  /**
   * This is a public API and used for delete the feature request record.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `apiV1IdDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  apiV1IdDelete(params: {

    /**
     * Unique Id of the feature request
     */
    id: string;
    context?: HttpContext
  }
): Observable<DeleteRequestSuccess> {

    return this.apiV1IdDelete$Response(params).pipe(
      map((r: StrictHttpResponse<DeleteRequestSuccess>) => r.body as DeleteRequestSuccess)
    );
  }

  /**
   * Path part for operation apiV1IdPatch
   */
  static readonly ApiV1IdPatchPath = '/api/v1/{id}';

  /**
   * This is a public API, user specific records can be updated.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `apiV1IdPatch()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  apiV1IdPatch$Response(params: {

    /**
     * Unique Id of the feature request
     */
    id: string;
    context?: HttpContext
    body: {
'title'?: string;
'description': string;
}
  }
): Observable<StrictHttpResponse<UpdateFeatureRequest>> {

    const rb = new RequestBuilder(this.rootUrl, FeatureRequestApiService.ApiV1IdPatchPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UpdateFeatureRequest>;
      })
    );
  }

  /**
   * This is a public API, user specific records can be updated.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `apiV1IdPatch$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  apiV1IdPatch(params: {

    /**
     * Unique Id of the feature request
     */
    id: string;
    context?: HttpContext
    body: {
'title'?: string;
'description': string;
}
  }
): Observable<UpdateFeatureRequest> {

    return this.apiV1IdPatch$Response(params).pipe(
      map((r: StrictHttpResponse<UpdateFeatureRequest>) => r.body as UpdateFeatureRequest)
    );
  }

  /**
   * Path part for operation postPopularFeatureVotes
   */
  static readonly PostPopularFeatureVotesPath = '/api/v1/voted-features';

  /**
   * This is a public API to upvote popular feature request.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postPopularFeatureVotes()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postPopularFeatureVotes$Response(params: {
    context?: HttpContext
    body: PostPopularFeatureVotes
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FeatureRequestApiService.PostPopularFeatureVotesPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This is a public API to upvote popular feature request.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postPopularFeatureVotes$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postPopularFeatureVotes(params: {
    context?: HttpContext
    body: PostPopularFeatureVotes
  }
): Observable<void> {

    return this.postPopularFeatureVotes$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
